import { api } from "shared/redux/api"
import { buildListParams } from "./procurement"
import { camelizeKeys } from "shared/utils/camelize"

// Item Master module — item_master / item_group / item_stock /
// item_center_availability / item_price_history / order_group.
//
// Paths versioned under /v1 per the 2026-08-2x procurement API cleanup
// (every module now follows ${METHOD}_V{n}_${MODULE}_${ACTION} naming
// server-side, with the version embedded in the path rather than the
// controller's base mapping). `/private/procurement` prefix so the bearer
// token attaches (the axios base query gates on the URL containing
// "private"), envelope unwrap + camelCase normalization on read, plain
// camelCase bodies on write.
const BASE = "/private/procurement/v1"

const unwrap = (response) => camelizeKeys(response?.data)

// Creating an OR or receiving goods changes Book Inventory's OR status and stock.
export const BOOK_INVENTORY_TAGS = [
  { type: "ITEM-STOCK", id: "BOOK-OVERVIEW" },
  { type: "ITEM-STOCK", id: "BOOK-DEMAND" },
  { type: "ITEM-STOCK", id: "BOOK-CLASSES" },
]

// Defensive unwrap for list endpoints whose exact envelope isn't confirmed —
// accepts a bare array, an `envelope.data` array, or a `.items`/`.content`
// page, and never throws on shapes it doesn't recognize (mirrors
// unwrapList in ./procurement.js).
const unwrapList = (response) => {
  const data = camelizeKeys(response?.data ?? response)
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.content)) return data.content
  return []
}

const procurementItemsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Items (item_master) ─────────────────────────────────────────────────
    // GET /items?center_id=&orderable_only=&category_id=&item_group_id=&q=&is_active=
    // center_id and orderable_only are independent: passing center_id alone
    // does NOT filter the list — every item still comes back, each annotated
    // with `isOrderable` (true/false from item_center_availability for that
    // item+center pair, or null if no row exists / no center_id was sent).
    // Only orderable_only=true actually filters down to orderable items —
    // requires center_id or the backend 400s. The Order Request line-item
    // picker uses center_id alone (annotate + gray out, don't filter); the
    // Item List admin screen uses category_id/item_group_id/search instead.
    // Supports paging (page/size) — omitted by default so existing callers
    // keep getting whatever the backend's unpaged/default page looks like.
    getItems: build.query({
      query: ({
        centerId,
        orderableOnly,
        categoryId,
        itemGroupId,
        search,
        activeOnly,
        // mustHaveOrder/mustHavePurchase (Boolean, optional) — scope down to
        // items flagged for the "create PO/Payment without Order/PO" flow
        // (2026-09 barrier-removal feature). Bỏ trống = không lọc.
        mustHaveOrder,
        mustHavePurchase,
        defaultVendorId,
        isApplyReferencePrice,
        page,
        size,
      } = {}) => ({
        url: `${BASE}/items`,
        params: buildListParams({
          center_id: centerId || undefined,
          orderable_only: orderableOnly ? true : undefined,
          category_id: categoryId || undefined,
          item_group_id: itemGroupId || undefined,
          q: search || undefined,
          is_active: activeOnly ? true : undefined,
          must_have_order: mustHaveOrder,
          must_have_purchase: mustHavePurchase,
          default_vendor_id: defaultVendorId || undefined,
          is_apply_reference_price: isApplyReferencePrice,
          page,
          size,
        }),
      }),
      transformResponse: unwrapList,
      providesTags: (result) =>
        result
          ? [
              ...result.map((it) => ({ type: "ITEM", id: it.id })),
              { type: "ITEM", id: "LIST" },
            ]
          : [{ type: "ITEM", id: "LIST" }],
    }),

    // GET /items/{id}
    getItem: build.query({
      query: (id) => ({ url: `${BASE}/items/${id}` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "ITEM", id }],
    }),

    // POST /items — required: uom, defaultVendorId, standardPrice,
    // categoryId, groupId (per the Item Detail form).
    createItem: build.mutation({
      query: (body) => ({ url: `${BASE}/items`, method: "POST", body }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "ITEM", id: "LIST" }],
    }),

    // PUT /items/{id}
    updateItem: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/items/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ITEM", id },
        { type: "ITEM", id: "LIST" },
      ],
    }),

    // PATCH /items/{id} — null/blank fields are left unchanged (so it can't
    // clear imageUrl; use PUT for that). Optimistic: success writes nothing
    // back to avoid refetching the whole list; failure rolls back and
    // refetches so the UI shows the server's real value.
    patchItem: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/items/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: unwrap,
      async onQueryStarted({ id, ...body }, { dispatch, getState, queryFulfilled }) {
        const patches = api.util
          .selectInvalidatedBy(getState(), [{ type: "ITEM", id }])
          .map(({ endpointName, originalArgs }) => {
            if (endpointName === "getItems") {
              return dispatch(
                api.util.updateQueryData("getItems", originalArgs, (list) => {
                  const row = list.find((it) => it.id === id)
                  if (row) Object.assign(row, body)
                })
              )
            }
            if (endpointName === "getItem") {
              return dispatch(
                api.util.updateQueryData("getItem", originalArgs, (item) => {
                  Object.assign(item, body)
                })
              )
            }
            return null
          })
          .filter(Boolean)
        try {
          await queryFulfilled
        } catch {
          patches.forEach((p) => p.undo())
          dispatch(api.util.invalidateTags([{ type: "ITEM", id }]))
        }
      },
    }),

    // GET /items/{item_id}/stock?center_id= — "Check Stock" button on an
    // Order Request line. Deliberately not auto-fetched: exposed as a lazy
    // query so it only fires on click.
    getItemStock: build.query({
      query: ({ itemId, centerId }) => ({
        url: `${BASE}/items/${itemId}/stock`,
        params: buildListParams({ center_id: centerId || undefined }),
      }),
      transformResponse: unwrap,
      providesTags: (_r, _e, { itemId, centerId }) => [
        { type: "ITEM-STOCK", id: `${itemId}:${centerId}` },
      ],
    }),

    // GET /item-stock?item_id=&item_group_id=&category_id=&center_id= —
    // read-only stock list (§2.4). item_group_id (pro.item_group.id) and
    // category_id (pro.budget_category.id, denormalized onto item) added
    // alongside the original item_id/center_id filters.
    getItemStockList: build.query({
      query: ({ itemId, itemGroupId, categoryId, centerId } = {}) => ({
        url: `${BASE}/item-stock`,
        params: buildListParams({
          item_id: itemId || undefined,
          item_group_id: itemGroupId || undefined,
          category_id: categoryId || undefined,
          center_id: centerId || undefined,
        }),
      }),
      transformResponse: unwrapList,
      providesTags: [{ type: "ITEM-STOCK", id: "LIST" }],
    }),

    // GET /book-inventory/demand?center_id=&item_id=&page=&size= — backed by
    // the pro.v_book_inventory_demand view: one row per center × book with
    // demand from classes starting in the next 7/30 days vs. stock on hand.
    // needed* / gap* count what's still needed after books already issued (a
    // fully issued item keeps its row with 0 needed). Rows with demand only in
    // the 31–90 day range also come back, with both needed* = 0. gap = stock − needed,
    // negative = short. center_id is hub.Center id.
    // Response: { items, total, page, size }, server-sorted by center, item.
    // Each row carries `imageUrl` (null until item images are uploaded).
    getBookInventoryDemand: build.query({
      query: ({ centerId, itemId, page, size } = {}) => ({
        url: `${BASE}/book-inventory/demand`,
        params: buildListParams({
          center_id: centerId || undefined,
          item_id: itemId || undefined,
          page,
          size,
        }),
      }),
      transformResponse: (response) => {
        const data = unwrap(response)
        return {
          items: Array.isArray(data?.items) ? data.items : [],
          total: data?.total ?? 0,
          page: data?.page ?? 0,
          size: data?.size ?? 0,
        }
      },
      providesTags: [{ type: "ITEM-STOCK", id: "BOOK-DEMAND" }],
    }),

    // GET /book-inventory/overview?center_id= — KPI cards. Empty center_id =
    // all centers. The original 5 fields come back 0 (never null). Order
    // tracking: centerBooksWithOr90d / centerBooksWithoutOr90d (center × book),
    // shortageCenterBooksWithoutOr{7,30,90}d, orRequestedQty. Shortage per
    // 7d/30d/90d: actualShortage(Books)* (null while some books lack data),
    // estimatedShortage(Books)* (null while estimatedUnknownItems* > 0),
    // *ShortageKnown* = the part computed from books with enough data (never
    // null). New nullable fields show "—"/"Chưa đủ dữ liệu", never 0.
    getBookInventoryOverview: build.query({
      query: ({ centerId } = {}) => ({
        url: `${BASE}/book-inventory/overview`,
        params: buildListParams({ center_id: centerId || undefined }),
      }),
      transformResponse: unwrap,
      providesTags: [{ type: "ITEM-STOCK", id: "BOOK-OVERVIEW" }],
    }),

    // GET /book-inventory/allocations?center_id=&item_id= — View Stock popup:
    // stock + per-class reserve/issue/release tracking. The header totals
    // (outstandingReservedQty, totalIssuedQty, transferableQty,
    // availableToReserveQty) cover every class of this center × item, not only
    // the ones listed. transferableQty = max(0, stock − reserved not yet issued).
    // classes[] = classes starting within 90 days (rowScope UPCOMING_90D) plus
    // older ones that still hold/issued books (ALLOCATION_HISTORY); a class
    // mapped to the item under 2 mapping types shows up as 2 rows. Each row's
    // allowedActions drives the Reserve/Issue/Release buttons.
    getBookInventoryAllocations: build.query({
      query: ({ centerId, itemId }) => ({
        url: `${BASE}/book-inventory/allocations`,
        params: buildListParams({ center_id: centerId, item_id: itemId }),
      }),
      transformResponse: (response) => {
        const data = unwrap(response)
        return { ...data, classes: Array.isArray(data?.classes) ? data.classes : [] }
      },
      providesTags: (_r, _e, { centerId, itemId }) => [{ type: "ITEM-STOCK", id: `BOOK-ALLOC-${centerId}:${itemId}` }],
    }),

    // POST /book-inventory/allocations/{reserve|issue|release}
    //   body { classId, centerId, itemId, quantity }
    // reserve: quantity > 0 required, holds books without touching stock.
    // issue:   no quantity — issues everything held, stock −= that, ONCE.
    // release: quantity optional (empty = release all held), no stock change.
    // 409 once a class is issued (no re-issue / reserve / release), when
    // issuing with nothing held, when stock is short, when reserving more than
    // availableToReserveQty, or when issuing while held ≠ the class's need
    // (classItemNeededQty). Issue changes stock,
    // so demand, overview and class detail refresh too.
    bookInventoryAllocationAction: build.mutation({
      query: ({ action, classId, centerId, itemId, quantity }) => ({
        url: `${BASE}/book-inventory/allocations/${action}`,
        method: "POST",
        body: { classId, centerId, itemId, quantity: quantity == null || quantity === "" ? undefined : Number(quantity) },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { centerId, itemId }) => [
        { type: "ITEM-STOCK", id: `BOOK-ALLOC-${centerId}:${itemId}` },
        { type: "ITEM-STOCK", id: `BOOK-CLASSES-${centerId}:${itemId}` },
        { type: "ITEM-STOCK", id: `BOOK-TRANSFER-SOURCES-${itemId}` },
        { type: "ITEM-STOCK", id: "BOOK-DEMAND" },
        { type: "ITEM-STOCK", id: "BOOK-OVERVIEW" },
        { type: "ITEM-STOCK", id: "LIST" },
      ],
    }),

    // GET /book-inventory/transfer-sources?item_id=&current_center_id= — the
    // other centers holding this item, sorted by transferableQty desc. Each
    // row: sourceCenterId/Name, currentStock, outstandingReservedQty,
    // transferableQty, transferStatus, stockUpdatedAt. One class at the source
    // not fully reserved within 30 days makes its transferableQty 0.
    getBookInventoryTransferSources: build.query({
      query: ({ itemId, currentCenterId }) => ({
        url: `${BASE}/book-inventory/transfer-sources`,
        params: buildListParams({ item_id: itemId, current_center_id: currentCenterId }),
      }),
      transformResponse: unwrapList,
      providesTags: (_r, _e, { itemId }) => [{ type: "ITEM-STOCK", id: `BOOK-TRANSFER-SOURCES-${itemId}` }],
    }),

    // POST /book-inventory/transfers — body { requestKey (UUID, new per
    // Confirm click), itemId, sourceCenterId, destinationCenterId, quantity,
    // note }. 400 source = destination; 409 reused requestKey, no stock at
    // the source, or quantity > transferableQty (message carries the
    // transfer_status). Response adds sourceStockAfter/destinationStockAfter.
    createBookInventoryTransfer: build.mutation({
      query: (body) => ({ url: `${BASE}/book-inventory/transfers`, method: "POST", body }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { itemId, sourceCenterId, destinationCenterId }) => [
        { type: "ITEM-STOCK", id: `BOOK-TRANSFER-SOURCES-${itemId}` },
        { type: "ITEM-STOCK", id: `BOOK-ALLOC-${sourceCenterId}:${itemId}` },
        { type: "ITEM-STOCK", id: `BOOK-ALLOC-${destinationCenterId}:${itemId}` },
        { type: "ITEM-STOCK", id: `BOOK-CLASSES-${sourceCenterId}:${itemId}` },
        { type: "ITEM-STOCK", id: `BOOK-CLASSES-${destinationCenterId}:${itemId}` },
        { type: "ITEM-STOCK", id: "BOOK-DEMAND" },
        { type: "ITEM-STOCK", id: "BOOK-OVERVIEW" },
        { type: "ITEM-STOCK", id: "LIST" },
      ],
    }),

    // GET /book-inventory/demand/classes?center_id=&item_id=&days_window= —
    // one /demand row's breakdown: { item, classes[] }. `item.quantityOnHand`
    // is this center's item_stock (0 + null stockUpdatedAt when there's no
    // stock row). days_window = 7/30/90, sorted by startDate, className. Only
    // books in book_mapping that apply to the class's A/B/READER phase are
    // listed. Each class carries grossNeededQty, issuedQty, remainingNeededQty
    // = max(0, gross − issued), allocationStatus, the current (source) class,
    // actual/estimated students & books with the +30% buffer (estimated is null
    // without a source class, both are null when calculationStatus isn't
    // CALCULATED), session range, bookRule and the OR status.
    getBookInventoryDemandClasses: build.query({
      query: ({ centerId, itemId, daysWindow }) => ({
        url: `${BASE}/book-inventory/demand/classes`,
        params: buildListParams({ center_id: centerId, item_id: itemId, days_window: daysWindow }),
      }),
      transformResponse: (response) => {
        const data = unwrap(response)
        return { item: data?.item || null, classes: Array.isArray(data?.classes) ? data.classes : [] }
      },
      providesTags: (_r, _e, { centerId, itemId }) => [
        { type: "ITEM-STOCK", id: `BOOK-CLASSES-${centerId}:${itemId}` },
        { type: "ITEM-STOCK", id: "BOOK-CLASSES" },
      ],
    }),

    // ── Item price history (item_price_history) ─────────────────────────────
    // GET /items/{id}/price-history
    getItemPriceHistory: build.query({
      query: (itemId) => ({ url: `${BASE}/items/${itemId}/price-history` }),
      transformResponse: unwrapList,
      providesTags: (_r, _e, itemId) => [{ type: "ITEM", id: `PRICE-${itemId}` }],
    }),

    // POST /items/{id}/price-history — { price, vendorId?, effectiveDate }
    addItemPrice: build.mutation({
      query: ({ itemId, ...body }) => ({
        url: `${BASE}/items/${itemId}/price-history`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { itemId }) => [
        { type: "ITEM", id: `PRICE-${itemId}` },
        { type: "ITEM", id: itemId },
      ],
    }),

    // ── Item groups (item_group) ─────────────────────────────────────────────
    // GET /item-groups?category_id=&active_only=
    getItemGroups: build.query({
      query: ({ categoryId, activeOnly } = {}) => ({
        url: `${BASE}/item-groups`,
        params: buildListParams({
          category_id: categoryId || undefined,
          active_only: activeOnly ? true : undefined,
        }),
      }),
      transformResponse: unwrapList,
      providesTags: (result) =>
        result
          ? [
              ...result.map((g) => ({ type: "ITEM-GROUP", id: g.id })),
              { type: "ITEM-GROUP", id: "LIST" },
            ]
          : [{ type: "ITEM-GROUP", id: "LIST" }],
    }),

    // POST /item-groups — { code, name, categoryId, active }
    createItemGroup: build.mutation({
      query: (body) => ({ url: `${BASE}/item-groups`, method: "POST", body }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "ITEM-GROUP", id: "LIST" }],
    }),

    // PUT /item-groups/{id}
    updateItemGroup: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/item-groups/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ITEM-GROUP", id },
        { type: "ITEM-GROUP", id: "LIST" },
      ],
    }),

    // ── Item center availability (item_center_availability) ─────────────────
    // GET /items/center-availability?region=&search=&centerId=&page=&size= —
    // flat list of item × center availability rows (one row per existing
    // pairing): { data: { items: [{ id, item: {id, itemCode, name}, center:
    // {id, centerName}, isOrderable }], total } }. Ordinary page/size
    // pagination over that flat list (size defaults to 500 pairs/page) —
    // ItemAvailabilityView accumulates every page fetched so far and derives
    // the item/center matrix (rows/columns) as the union of it, fetching the
    // next page automatically once the table is scrolled near its right edge
    // and `total` says there's more than what's loaded. `centerId` narrows
    // to a single center's pairs (single column, no further paging needed).
    // This contract (shape + params) is an ASSUMPTION, not confirmed against
    // a real backend build yet.
    getItemCenterAvailabilityMatrix: build.query({
      query: ({ region, search, centerId, page = 0, size = 500 } = {}) => ({
        url: `${BASE}/items/center-availability`,
        params: buildListParams({
          region: region || undefined,
          search: search || undefined,
          centerId: centerId || undefined,
          page,
          size,
        }),
      }),
      transformResponse: (response) => {
        const data = unwrap(response)
        return {
          rows: Array.isArray(data?.items) ? data.items : [],
          total: data?.total ?? 0,
        }
      },
      providesTags: [{ type: "ITEM-AVAILABILITY", id: "MATRIX" }],
    }),

    // TODO: update endpoint for center availability not confirmed yet by
    // backend — path/body below is an ASSUMPTION, needs confirmation.
    updateItemCenterAvailability: build.mutation({
      query: ({ id, entries }) => ({
        url: `${BASE}/items/${id}/center-availability`,
        method: "PUT",
        body: { entries },
      }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "ITEM-AVAILABILITY", id: "MATRIX" }],
    }),

    // ── Group items (mới) ────────────────────────────────────────────────────
    // GET /group-items?categoryId= — every item group (optionally scoped to
    // one category), each with its full nested `items` array from
    // item_master (itemCode, name, uom, standardPrice, default vendor, ...).
    // Replaces the old flat placeholder `getGroupItems` that used to live in
    // ./procurement.js (blocked on master-data work, path never confirmed) —
    // this is the real endpoint. Different `categoryId` args are separate
    // RTK Query cache entries, so switching category re-fetches automatically.
    getGroupItems: build.query({
      query: ({ categoryId } = {}) => ({
        url: `${BASE}/group-items`,
        params: buildListParams({ categoryId: categoryId || undefined }),
      }),
      transformResponse: unwrapList,
      providesTags: [{ type: "GROUP-ITEM", id: "LIST" }],
    }),

    // ── Centers (hub.Center, region-scoped via hub.center_region) ───────────
    // GET /centers?region=&page=&size= — active centers only. Same
    // North/South region join as item-center-availability; region=Central
    // isn't a real hub.center_region bucket, so callers omit the param
    // entirely for Central (find-all, no filter) instead of sending it.
    // Response: { items: [{ centerId, centerName, budgetCenterId,
    // budgetCenterName, sunCenterCode, sunCenterName, isActive, hubCenterId,
    // hubCenterCode, hubCenterName, region }], total, page, size }. `centerId`
    // is the hub.Center id this endpoint is scoped by — consumers picking a
    // center for an order line should key off that, not `budgetCenterId`
    // (a different id space, one hub center can map to one budget center).
    // Named distinctly from lxo.js's `getCenters` (/private/event/v1/block/center)
    // — RTK Query endpoint names are global across every `injectEndpoints`
    // call on the same `api` instance, so a same-name injection here would
    // silently resolve to that one instead (overrideExisting: false means
    // whichever loads first wins) — exactly what happened before this rename.
    getProcurementCenters: build.query({
      query: ({ region, page, size } = {}) => ({
        url: `${BASE}/centers`,
        params: buildListParams({ region: region || undefined, page, size }),
      }),
      transformResponse: (response) => {
        const data = unwrap(response)
        return {
          items: Array.isArray(data?.items) ? data.items : [],
          total: data?.total ?? 0,
          page: data?.page ?? 0,
          size: data?.size ?? 0,
        }
      },
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((c) => ({ type: "CENTER", id: c.centerId })),
              { type: "CENTER", id: "LIST" },
            ]
          : [{ type: "CENTER", id: "LIST" }],
    }),

    // ── Suppliers (pro.supplier) ─────────────────────────────────────────────
    // GET /suppliers?is_active=&category_id=&group_item_id= — used by the
    // Order Request Vendor Proposals dropdown, scoped to the order's selected
    // category so only suppliers actually stocking that category show up.
    // Named distinctly from procurementPurchases.js's `getPurchaseSuppliers`
    // (/purchases/suppliers, unfiltered) and procurementV2.js's mocked
    // `getSuppliersV2`/`getSupplierDetail` and procurement.js's legacy
    // `getSuppliers` — RTK Query endpoint names are global across every
    // `injectEndpoints` call on the same `api` instance, so reusing any of
    // those names here would silently resolve to the wrong one instead of
    // erroring (overrideExisting: false means whichever loads first wins).
    getProcurementSuppliers: build.query({
      query: ({ isActive, categoryId, groupItemId } = {}) => ({
        url: `${BASE}/suppliers`,
        params: buildListParams({
          is_active: isActive,
          category_id: categoryId || undefined,
          group_item_id: groupItemId || undefined,
        }),
      }),
      transformResponse: unwrapList,
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: "PROC-SUPPLIER", id: s.id })),
              { type: "PROC-SUPPLIER", id: "LIST" },
            ]
          : [{ type: "PROC-SUPPLIER", id: "LIST" }],
    }),

    // GET /suppliers/{id} — 404 if it doesn't exist.
    getProcurementSupplierDetail: build.query({
      query: (id) => ({ url: `${BASE}/suppliers/${id}` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "PROC-SUPPLIER", id }],
    }),

    // GET /suppliers/{supplierId}/item-prices — catalogue price per item for
    // one supplier, scoped by exactly ONE of order_request_id /
    // group_order_id / purchase_id / item_id (400 otherwise). Group/PO sources
    // sum quantity per itemId across their member/source orders; off-catalog
    // lines are skipped. `itemIds` needs no saved source (e.g. a line on an
    // unsaved order). Rows with `priceFound: false` carry null price fields.
    getProcurementSupplierItemPrices: build.query({
      query: ({ supplierId, orderRequestId, groupOrderId, purchaseId, itemIds }) => ({
        url: `${BASE}/suppliers/${supplierId}/item-prices`,
        params: buildListParams({
          order_request_id: orderRequestId,
          group_order_id: groupOrderId,
          purchase_id: purchaseId,
          item_id: itemIds?.length ? itemIds.join(",") : undefined,
        }),
      }),
      transformResponse: unwrapList,
    }),

    // POST /suppliers — name + taxCode required, everything else optional.
    // 409 if taxCode already exists on another supplier.
    createProcurementSupplier: build.mutation({
      query: (body) => ({
        url: `${BASE}/suppliers`,
        method: "POST",
        body: {
          name: body.name,
          taxCode: body.taxCode,
          email: body.email || undefined,
          bankName: body.bankName || undefined,
          bankAccount: body.bankAccount || undefined,
          region: body.region || undefined,
          budgetCategoryId: body.budgetCategoryId || undefined,
          isActive: body.isActive ?? true,
        },
      }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "PROC-SUPPLIER", id: "LIST" }],
    }),

    // PUT /suppliers/{id} — partial update, only send fields being changed.
    // 409 if taxCode collides with another supplier, 404 if id doesn't exist.
    updateProcurementSupplier: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/suppliers/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "PROC-SUPPLIER", id },
        { type: "PROC-SUPPLIER", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetItemsQuery,
  useLazyGetItemsQuery,
  useGetItemQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  usePatchItemMutation,
  useGetItemStockQuery,
  useLazyGetItemStockQuery,
  useGetItemStockListQuery,
  useGetBookInventoryDemandQuery,
  useLazyGetBookInventoryDemandQuery,
  useGetBookInventoryOverviewQuery,
  useGetBookInventoryDemandClassesQuery,
  useGetBookInventoryAllocationsQuery,
  useBookInventoryAllocationActionMutation,
  useGetBookInventoryTransferSourcesQuery,
  useCreateBookInventoryTransferMutation,
  useGetItemPriceHistoryQuery,
  useAddItemPriceMutation,
  useGetItemGroupsQuery,
  useCreateItemGroupMutation,
  useUpdateItemGroupMutation,
  useGetItemCenterAvailabilityMatrixQuery,
  useLazyGetItemCenterAvailabilityMatrixQuery,
  useUpdateItemCenterAvailabilityMutation,
  useGetGroupItemsQuery,
  useGetProcurementCentersQuery,
  useGetProcurementSuppliersQuery,
  useLazyGetProcurementSuppliersQuery,
  useGetProcurementSupplierDetailQuery,
  useLazyGetProcurementSupplierItemPricesQuery,
  useCreateProcurementSupplierMutation,
  useUpdateProcurementSupplierMutation,
} = procurementItemsApi

export default procurementItemsApi
